import React, { useEffect, useState } from 'react';
import { Plus, PencilSimple, Trash, X, FloppyDisk, CircleNotch, WifiHigh, ToggleLeft, ToggleRight } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

const EMPTY_FORM = {
    employee_id: '',
    name: '',
    kwota: '',
    salary_period: '01-15',
    active: true,
    add_to_salary: true,
    start_year: new Date().getFullYear(),
    start_month: new Date().getMonth() + 1,
    notes: '',
};

const MONTHS = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

export default function UslugiSiecioweTab() {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [filterEmp, setFilterEmp] = useState('');

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [u, e] = await Promise.all([
                axios.get(`${API}/uslugi-sieciowe`),
                axios.get(`${API}/employees`),
            ]);
            setItems(u.data || []);
            setEmployees(e.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setShowModal(true);
    };

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            employee_id: u.employee_id || '',
            name: u.name || '',
            kwota: u.kwota != null ? String(u.kwota) : '',
            salary_period: u.salary_period || '01-15',
            active: u.active !== false,
            add_to_salary: u.add_to_salary !== false,
            start_year: u.start_year || new Date().getFullYear(),
            start_month: u.start_month || new Date().getMonth() + 1,
            notes: u.notes || '',
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.employee_id) return alert('Wybierz pracownika');
        if (!form.name.trim()) return alert('Wpisz nazwę');
        const kw = parseFloat(form.kwota);
        if (isNaN(kw) || kw <= 0) return alert('Wpisz poprawną kwotę');
        setSaving(true);
        try {
            const payload = {
                employee_id: form.employee_id,
                name: form.name.trim(),
                kwota: kw,
                salary_period: form.salary_period,
                active: form.active,
                add_to_salary: form.add_to_salary,
                start_year: parseInt(form.start_year),
                start_month: parseInt(form.start_month),
                notes: form.notes || null,
            };
            if (editing) {
                await axios.put(`${API}/uslugi-sieciowe/${editing.id}`, payload);
            } else {
                await axios.post(`${API}/uslugi-sieciowe`, payload);
            }
            setShowModal(false);
            setEditing(null);
            setForm(EMPTY_FORM);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd zapisu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Usunąć tę usługę? Historia płatności też zostanie usunięta.')) return;
        try {
            await axios.delete(`${API}/uslugi-sieciowe/${id}`);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd usuwania');
        }
    };

    const handleToggleActive = async (u) => {
        try {
            await axios.put(`${API}/uslugi-sieciowe/${u.id}`, { active: !u.active });
            fetchAll();
        } catch (err) {
            alert('Błąd');
        }
    };

    const handleMarkPaid = async (u) => {
        const now = new Date();
        const yStr = window.prompt('Rok:', String(now.getFullYear()));
        if (!yStr) return;
        const mStr = window.prompt('Miesiąc (1-12):', String(now.getMonth() + 1));
        if (!mStr) return;
        try {
            await axios.post(`${API}/uslugi-sieciowe/${u.id}/mark-paid`, {
                year: parseInt(yStr), month: parseInt(mStr),
            });
            fetchAll();
        } catch (err) {
            alert('Błąd');
        }
    };

    const empName = (emp) => emp?.imie_nazwisko ? `${emp.imie_nazwisko} (${emp.name})` : emp?.name || '—';

    const filtered = filterEmp ? items.filter(x => x.employee_id === filterEmp) : items;

    if (loading) {
        return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;
    }

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <h2 className="font-heading text-lg sm:text-xl font-bold flex items-center gap-2">
                        <WifiHigh className="w-5 h-5 text-foreground" weight="bold" /> Usługi sieciowe / internetowe
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">Miesięczne stałe potrącenia z wynagrodzenia (np. abonament telefoniczny, internet).</p>
                </div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2 text-sm" data-testid="usluga-add-btn">
                    <Plus className="w-4 h-4" /> Dodaj
                </button>
            </div>

            <div className="mb-3">
                <select value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-sm">
                    <option value="">Wszyscy pracownicy ({items.length})</option>
                    {employees.map(emp => <option key={emp._id} value={emp._id}>{empName(emp)}</option>)}
                </select>
            </div>

            {filtered.length === 0 ? (
                <div className="text-center py-16 text-slate-500 bg-white border border-slate-200 rounded-sm">
                    Brak usług. Dodaj pierwszą!
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block bg-white border border-slate-200 rounded-sm overflow-x-auto">
                        <table className="w-full text-sm min-w-[800px]">
                            <thead className="bg-muted">
                                <tr className="text-left text-xs font-semibold text-slate-600 uppercase">
                                    <th className="px-4 py-3">Pracownik</th>
                                    <th className="px-4 py-3">Nazwa</th>
                                    <th className="px-4 py-3 text-right">Kwota</th>
                                    <th className="px-4 py-3 text-center">Okres</th>
                                    <th className="px-4 py-3 text-center">Aktywna</th>
                                    <th className="px-4 py-3 text-center">Opłacone m-cy</th>
                                    <th className="px-4 py-3 text-right">Akcje</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.map(u => {
                                    const paidCount = (u.payments || []).filter(p => p.paid).length;
                                    return (
                                        <tr key={u.id} className="hover:bg-slate-50" data-testid={`usluga-row-${u.id}`}>
                                            <td className="px-4 py-3 text-slate-700 text-xs">{empName(u.employee)}</td>
                                            <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                                            <td className="px-4 py-3 text-right font-mono font-semibold">{u.kwota?.toFixed(2)} zł</td>
                                            <td className="px-4 py-3 text-center text-xs">{u.salary_period}</td>
                                            <td className="px-4 py-3 text-center">
                                                <button onClick={() => handleToggleActive(u)} title={u.active !== false ? 'Aktywna — kliknij aby wyłączyć' : 'Wyłączona — kliknij aby włączyć'}>
                                                    {u.active !== false ? <ToggleRight className="w-6 h-6 text-emerald-600" weight="fill" /> : <ToggleLeft className="w-6 h-6 text-slate-400" weight="fill" />}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-center text-xs text-slate-600">{paidCount}</td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                <button onClick={() => handleMarkPaid(u)} title="Oznacz miesiąc jako opłacony ręcznie" className="p-1.5 hover:bg-muted text-foreground rounded-sm text-xs px-2">Opłać</button>
                                                <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-slate-100 rounded-sm ml-1" data-testid={`usluga-edit-${u.id}`}><PencilSimple className="w-4 h-4" /></button>
                                                <button onClick={() => handleDelete(u.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm ml-1" data-testid={`usluga-delete-${u.id}`}><Trash className="w-4 h-4" /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2">
                        {filtered.map(u => {
                            const paidCount = (u.payments || []).filter(p => p.paid).length;
                            return (
                                <div key={u.id} className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`usluga-card-${u.id}`}>
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded text-xs">{u.employee?.name || '—'}</span>
                                                <span className="font-semibold text-sm truncate">{u.name}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-slate-600">
                                                <span className="font-mono font-bold text-slate-800">{u.kwota?.toFixed(2)} zł</span>
                                                <span>· Okres: {u.salary_period}</span>
                                                <span>· Opłacone: {paidCount}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => handleToggleActive(u)}>
                                            {u.active !== false ? <ToggleRight className="w-6 h-6 text-emerald-600" weight="fill" /> : <ToggleLeft className="w-6 h-6 text-slate-400" weight="fill" />}
                                        </button>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button onClick={() => handleMarkPaid(u)} className="flex-1 py-1.5 text-xs bg-muted hover:bg-muted text-foreground rounded font-medium">Opłać ręcznie</button>
                                        <button onClick={() => openEdit(u)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded" data-testid={`usluga-edit-${u.id}`}><PencilSimple className="w-4 h-4" /></button>
                                        <button onClick={() => handleDelete(u.id)} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded" data-testid={`usluga-delete-${u.id}`}><Trash className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="usluga-modal">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading text-xl font-bold">{editing ? 'Edytuj usługę' : 'Nowa usługa'}</h3>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-sm"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="field-label block mb-2">Pracownik *</label>
                                <select value={form.employee_id} onChange={(e) => setForm(p => ({ ...p, employee_id: e.target.value }))} className="input-field" data-testid="usluga-employee-input">
                                    <option value="">— Wybierz —</option>
                                    {employees.map(emp => <option key={emp._id} value={emp._id}>{empName(emp)}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="field-label block mb-2">Nazwa *</label>
                                <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="np. Play, Orange, Internet" className="input-field" data-testid="usluga-name-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="field-label block mb-2">Kwota (zł) *</label>
                                    <input type="number" step="0.01" min="0" value={form.kwota} onChange={(e) => setForm(p => ({ ...p, kwota: e.target.value }))} className="input-field" data-testid="usluga-kwota-input" />
                                </div>
                                <div>
                                    <label className="field-label block mb-2">Okres *</label>
                                    <select value={form.salary_period} onChange={(e) => setForm(p => ({ ...p, salary_period: e.target.value }))} className="input-field" data-testid="usluga-period-input">
                                        <option value="01-15">01-15</option>
                                        <option value="16-30">16-30</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="field-label block mb-2">Start — miesiąc</label>
                                    <select value={form.start_month} onChange={(e) => setForm(p => ({ ...p, start_month: e.target.value }))} className="input-field">
                                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="field-label block mb-2">Start — rok</label>
                                    <input type="number" value={form.start_year} onChange={(e) => setForm(p => ({ ...p, start_year: e.target.value }))} className="input-field" />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-slate-50">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm(p => ({ ...p, active: e.target.checked }))} />
                                <span className="text-sm">Aktywna (będzie automatycznie sugerowana)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-emerald-50 border border-emerald-200">
                                <input
                                    type="checkbox"
                                    checked={form.add_to_salary}
                                    onChange={(e) => setForm(p => ({ ...p, add_to_salary: e.target.checked }))}
                                    data-testid="usluga-add-to-salary-input"
                                />
                                <span className="text-sm">Automatycznie dodawaj do wypłat (Potrącenia)</span>
                            </label>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anuluj</button>
                            <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="usluga-save-btn">
                                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />} Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
